
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { getDateLocale, getExternalTermsUrl } from 'wtb-utils';
import { Typography } from 'wtb-components';
import { ReactComponent as Email } from 'assets/images/email.svg';
import { ReactComponent as Phone } from 'assets/images/phone.svg';
import {
  AboutMerchant,
  Block,
  ContactMessage,
  Divider,
  MerchantRedirect,
  PrintOrderDetails,
  PurchaseInfo,
  PrintYourInformation
} from 'components';
import { useLocaleContext, useTransactionContext } from 'context';
import { CANCELLATION_POLICY_PERIODS } from 'constants.js';
import { getRegretDate, isEmail } from 'utils';
import styles from './Summary.module.css';

const Receipt = () => {

  const { translate } = useLocaleContext();

  return (
    <>
      <Block
        title={translate('orderDetails.title')}
        collapsable={false}
        main
      >
        <PrintOrderDetails />
      </Block>

      <PrintYourInformation />

      <Block
        className={styles.orderDetails}
        title={translate('orderSummary.title')}
        collapsable={false}
        main
      >
        <PurchaseInfo detailed />
      </Block>
    </>
  );
};

const Support = () => {

  const {
    links,
    order: { orderDate },
    merchant: { name, info, terms },
    wtbTerms,
    campaign: {
      email, phone, cancellationDays, cancellationPolicy
    }
  } = useTransactionContext();
  const { baseLocale, locale, translate } = useLocaleContext();

  const [ dateLocale, setDateLocale ] = useState();

  useEffect(() => {
    const getLocale = async () => {
      if (locale) {

        const l = await require('date-fns/locale')[getDateLocale(locale)];
        setDateLocale(l);
      }
    };

    getLocale();
  }, [ locale ]);

  // When the terms are a single URL we link to it directly, skipping the Waytobill terms page
  const merchantTermsLink = getExternalTermsUrl(terms, locale)
    || links?.merchant?.page?.replace('{language}', baseLocale);
  const wtbTermsLink = getExternalTermsUrl(wtbTerms, locale)
    || links?.wtb?.page?.replace('{language}', baseLocale);

  const hasLinks = merchantTermsLink || wtbTermsLink;
  const hasCancelationPolicy = cancellationPolicy !== CANCELLATION_POLICY_PERIODS.NONE;

  return (
    <div className={styles.support}>
      <div className={styles.contactInfo}>
        <ContactMessage className={styles.contactMessage} hideEmail />

        {email && (
          <div className={styles.contactLine}>
            <Email aria-label="email-icon" />
            <Typography
              className={styles.contactText}
              variant="body2"
              component="div"
            >
              <a className={styles.link} href={isEmail(email) ? `mailto:${email}` : email} aria-label="email">{email}</a>
            </Typography>
          </div>
        )}

        {phone && (
          <div className={styles.contactLine}>
            <Phone aria-label="phone-icon" />
            <Typography
              className={styles.contactText}
              variant="body2"
              component="div"
            >
              <a className={styles.link} href={`tel:${phone}`}>{phone}</a>
            </Typography>
          </div>
        )}
      </div>

      {hasCancelationPolicy && dateLocale && (
        <>
          <Divider size="mid" />

          <div className={styles.boxBorder}>
            <div className={styles.accountBorder}>
              <Typography variant="subtitle2" component="div">
                {translate('orderSummary.regret.title')}
              </Typography>
            </div>

            <div className={styles.linksInfo} data-testid="regret-date">
              <Typography variant="body2" component="div">
                {translate('orderSummary.regret.text', { date: getRegretDate(orderDate, locale, dateLocale, cancellationDays) })}
              </Typography>
            </div>
          </div>
        </>
      )}

      <Divider />

      {hasLinks && (
        <>
          <div className={styles.boxBorder}>
            <div className={styles.accountBorder}>
              <Typography variant="subtitle2" component="div">
                {translate('print.footer.title')}
              </Typography>
            </div>

            <div className={styles.linksInfo}>
              {merchantTermsLink && (
                <Typography component="div" variant="body2">
                  <Trans
                    i18nKey="print.footer.terms"
                    values={{ merchant: name }}
                    components={[ <a
                      className={styles.link}
                      href={merchantTermsLink}
                      aria-label={`${name} terms`}
                      target="_blank"
                      rel="noreferrer"
                    /> ]}
                  />
                </Typography>
              )}
              {wtbTermsLink && (
                <Typography className={styles.terms} component="div" variant="body2">
                  <Trans
                    i18nKey="print.footer.terms"
                    values={{ merchant: 'Waytobill' }}
                    components={[ <a
                      className={styles.link}
                      href={wtbTermsLink}
                      aria-label="Waytobill terms"
                      target="_blank"
                      rel="noreferrer"
                    /> ]}
                  />
                </Typography>
              )}
            </div>
          </div>
          <Divider />
        </>
      )}

      {info && info[locale] && (
        <div className={styles.boxBorder}>
          <div className={styles.accountBorder}>
            <Typography variant="subtitle2" component="div">
              {translate('about.title', { merchant: name })}
            </Typography>
          </div>
          <AboutMerchant />
        </div>
      )}
    </div>
  );
};

const Summary = () => {
  const { merchant: { redirect } } = useTransactionContext();
  const { translate } = useLocaleContext();

  return (
    <div className={styles.root}>
      <Divider />
      {redirect?.successUrl && (
        <>
          <Divider />
          <MerchantRedirect />
          <Divider />
        </>
      )}

      <Block
        headerClass={styles.sectionTitle}
        title={<Typography fontWeight="bold">{translate('orderSummary.yourReceipt')}</Typography>}
        titleProps={{ id: 'receipt-header', className: styles.blockTitle }}
        collapsable
        id="summary-receipt"
      >
        <Receipt />
      </Block>

      <Divider size="mid" />

      <Block
        headerClass={styles.sectionTitle}
        title={<Typography fontWeight="bold">{translate('orderSummary.support')}</Typography>}
        titleProps={{ id: 'support-header' }}
        collapsable
        id="summary-support"
      >
        <Support />
      </Block>

      {/* <Block
        className={styles.orderDetails}
        title={translate('next.title')}
        collapsable={false}
        main
      >
        <WhatsNext />
      </Block> */}

      {/* {info && info[locale] && (
        <div className={styles.boxBorder}>
          <div className={styles.accountBorder}>
            <Typography variant="body1" component="div" fontWeight="bold">
              {translate('about.title', { merchant: name })}
            </Typography>
          </div>
          <AboutMerchant />
        </div>
      )} */}
    </div>
  );
};

export default Summary;
